// ============================================================================
// GLOSSARY DATA — single source of truth for stat definitions.
// Consumed by pages/Glossary.js (full page) and components/FloatingGlossary.js
// (Cmd/Ctrl+G dialog). Keep this in sync with the BACKEND engines:
//   - CVR / ACVR / Cycle Index  → src/express-backend/src/utils/cvrEngine.js (v7)
//   - CTS / Surplus / Verdict   → src/express-backend/src/utils/tradeEngine.js
//   - Stuff+                    → scripts/pullPlayByPlaySplits.cjs
// Icons are referenced by string key (iconKey) so this file stays JSX-free.
// ============================================================================

export const GLOSSARY_SECTIONS = [
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: 'proprietary',
    title: 'The Cycle Proprietary Metrics',
    iconKey: 'science',
    description: 'Original metrics you will only find on The Cycle.',
    stats: [
      {
        name: 'CVR',
        fullName: 'Cycle Value Rating (v7)',
        scale: '1–99 · 50 = median',
        applies: 'Player',
        category: 'proprietary',
        description:
          'Pure performance percentile against qualified peers in the same role (batters, starters, and relievers are ranked in separate pools). 50 is the league-median qualified player, 99 is the best in baseball. Salary plays no part — CVR answers only "how good is this player right now?"',
        formula:
          'Batters:  45% wOBA + 35% pace-WAR + 10% ISO + 5% BB/K + 5% SB run value\nPitchers: 40% FIP + 20% ERA + 20% K–BB% + 20% pace-WAR (lower FIP/ERA = better)\nWeighted z-scores vs qualified peers → percentile rank → CVR = 1 + percentile × 98',
        components: [
          { name: 'wOBA / FIP', max: '45% / 40%', desc: 'Primary skill anchor — overall hitting quality, or defense-independent run prevention' },
          { name: 'Pace-WAR', max: '35% / 20%', desc: 'WAR projected to a full workload: 162 G hitters, 180 IP starters, 65 IP relievers' },
          { name: 'ISO / ERA', max: '10% / 20%', desc: 'Raw power for hitters; actual run prevention for pitchers' },
          { name: 'BB/K / K–BB%', max: '5% / 20%', desc: 'Plate discipline for hitters; stuff + command for pitchers' },
          { name: 'SB run value', max: '5% / —', desc: 'Stolen-base linear weights per 162 games (hitters only)' },
        ],
        tiers: [
          { label: 'ELITE', range: '85+', color: '#FFD700' },
          { label: 'ALL-STAR', range: '70–84', color: '#C0C0C0' },
          { label: 'ABOVE AVG', range: '55–69', color: '#4CAF50' },
          { label: 'AVERAGE', range: '40–54', color: '#2196F3' },
          { label: 'BELOW AVG', range: '25–39', color: '#FF9800' },
          { label: 'REPLACEMENT', range: '10–24', color: '#FF5722' },
          { label: 'MINIMAL', range: '< 10', color: '#F44336' },
        ],
        details: [
          'Small samples are shrunk toward 50 — the percentile is pulled to the median by √(sample ÷ qualification), so a hot week can\'t produce a 95 CVR.',
          'Qualification adapts to the season: half the 90th-percentile workload among all players (floors: 40 PA, 15 IP for SP, 8 IP for RP).',
          'Everywhere in the app CVR defaults to "rolling" mode — the last-30-day window — so the number reflects current form, not April.',
        ],
        teamNote:
          'Team CVR is the playing-time-weighted average of every rostered player\'s CVR (batters weighted by PA, pitchers by IP × 3).',
      },
      {
        name: 'ACVR',
        fullName: 'Adjusted CVR — the "Steal Score" (v7)',
        scale: '1–99 · 50 = market-fair',
        applies: 'Player',
        category: 'proprietary',
        description:
          'Contract efficiency percentile: how much surplus production a team gets for the dollars it pays. 99 = the biggest steal in baseball (superstar on a league-minimum deal), 1 = the most underwater contract. Where CVR ignores salary entirely, salary is the whole point of ACVR.',
        formula:
          'Surplus $ = pace-WAR × $10M market rate − salary\n(league minimum $760K assumed when salary is unknown)\nACVR = percentile of surplus $ vs all qualified players → 1–99',
        tiers: [
          { label: 'MASSIVE STEAL', range: '85+', color: '#FFD700' },
          { label: 'GREAT VALUE', range: '70–84', color: '#4CAF50' },
          { label: 'GOOD VALUE', range: '55–69', color: '#8BC34A' },
          { label: 'MARKET-FAIR', range: '40–54', color: '#2196F3' },
          { label: 'SLIGHT OVERPAY', range: '25–39', color: '#FF9800' },
          { label: 'OVERPAID', range: '< 25', color: '#F44336' },
        ],
        details: [
          'Pre-arbitration stars dominate the top of the leaderboard — elite production at ~$760K is the definition of surplus.',
          'Small samples shrink toward 50 (below ~60 games for hitters or ~40 IP for pitchers).',
          'Rolling mode uses last-30-day pace-WAR, so a player heating up sees his ACVR rise daily.',
        ],
      },
      {
        name: 'Cycle Index',
        fullName: 'Team Market Efficiency Index (v7)',
        scale: '1–99 · 50 = expected',
        applies: 'Team',
        category: 'proprietary',
        description:
          'One number answering: is this team winning more games than its payroll says it should? Above 50 means the front office is beating the market; below 50 means dollars are being wasted.',
        formula:
          'Expected wins = 81 + (payroll − league-median payroll) ÷ $10M\nCycle Index = 50 + (162-game pace wins − expected wins) × 1.5   (clamped 1–99)',
        details: [
          'A median-payroll team is expected to win 81; each extra $10M of payroll "buys" one expected win.',
          'A low-payroll team on a 95-win pace scores far higher than a high-payroll team on the same pace.',
          'Updated daily and shown on team pages and the home screen.',
        ],
      },
      {
        name: 'CTS',
        fullName: 'Cycle Trade Score',
        scale: '0–100',
        applies: 'Player',
        category: 'proprietary',
        description:
          'The trade-asset score used across the Trade Analyzer. Blends on-field production with the contract, age, and roster context that actually drive trade negotiations.',
        formula: 'CTS = 30% CVR Production + 20% ACVR Contract Efficiency + 15% Positional Scarcity\n      + 15% Age Trajectory + 10% Versatility + 10% Team Control',
        components: [
          { name: 'CVR Production', max: '30%', desc: 'Current on-field performance (CVR v7)' },
          { name: 'ACVR Efficiency', max: '20%', desc: 'Contract surplus percentile (ACVR v7)' },
          { name: 'Scarcity', max: '15%', desc: 'How hard the position is to fill (C, SS, SP premium)' },
          { name: 'Age Trajectory', max: '15%', desc: 'Position on the aging curve — pre-peak beats post-peak' },
          { name: 'Versatility', max: '10%', desc: 'Multi-position eligibility / two-way usage' },
          { name: 'Team Control', max: '10%', desc: 'Years before free agency' },
        ],
        tiers: [
          { label: 'MVP CALIBER', range: '85+', color: '#FFD700' },
          { label: 'ALL-STAR', range: '75–84', color: '#FF6B35' },
          { label: 'QUALITY REGULAR', range: '65–74', color: '#4CAF50' },
          { label: 'SOLID STARTER', range: '55–64', color: '#2196F3' },
          { label: 'AVERAGE', range: '45–54', color: '#9E9E9E' },
          { label: 'BELOW AVERAGE', range: '35–44', color: '#795548' },
          { label: 'FRINGE', range: '25–34', color: '#FF5722' },
          { label: 'REPLACEMENT', range: '< 25', color: '#F44336' },
        ],
        details: [
          'Uses regressed projected WAR — a hot 15-game stretch is shrunk toward the positional average, so small samples can\'t inflate a player\'s trade stock.',
        ],
      },
      {
        name: 'Surplus Value',
        fullName: 'Total Contract Surplus — the trade currency',
        scale: '−$50M to $200M+',
        applies: 'Player',
        category: 'proprietary',
        description:
          'The headline dollar figure in every trade evaluation: the market value of a player\'s projected production over his remaining team control, minus what he\'ll actually be paid. This is what front offices are really exchanging in a trade.',
        formula:
          'Current year:  (projected WAR × $10M) − salary\n+ each control year: aging-curve WAR × $10M − salary (escalated 3%/yr)\nFuture years discounted 8% annually',
        benchmarks: [
          { label: '$100M+', desc: 'Franchise cornerstone — nearly untouchable' },
          { label: '$50M+', desc: 'Elite trade chip' },
          { label: '$20M+', desc: 'Strong asset' },
          { label: '~$0', desc: 'Market-fair contract — value and cost cancel out' },
          { label: '< $0', desc: 'Negative-value contract — team would pay to move it' },
        ],
      },
      {
        name: 'Trade Verdict',
        fullName: 'Trade Fairness Verdict',
        scale: 'Fair → Lopsided',
        applies: 'Trade',
        category: 'proprietary',
        description:
          'The Trade Analyzer\'s plain-English ruling, based on the total surplus-value gap between the two sides. It also flags "win-now vs. future" splits where one side gets more WAR today while the other banks long-term surplus.',
        tiers: [
          { label: 'FAIR', range: 'gap < $4M', color: '#4CAF50' },
          { label: 'SLIGHT EDGE', range: '$4M–$12M', color: '#8BC34A' },
          { label: 'CLEAR WIN', range: '$12M–$30M', color: '#FF9800' },
          { label: 'LOPSIDED', range: '$30M+', color: '#F44336' },
        ],
      },
      {
        name: 'DTV',
        fullName: 'Dynamic Trade Value',
        scale: 'Variable',
        applies: 'Player',
        category: 'proprietary',
        description:
          'An expanded, narrative-rich trade evaluation with a 6-component breakdown (Production, Surplus, Scarcity, Market, Aging, Control), future value projections, aging curves, positional market context, and a written analysis. Used in Trades → Dynamic Value for deep-dive player evaluation.',
        details: [
          'Production: WAR-based output score, pace-projected to a full season.',
          'Surplus: Market value ($10M/WAR) minus actual salary, contextualized by positional market rate.',
          'Scarcity: How rare the player\'s position is on the trade market.',
          'Market Premium: Positional market rate comparison (e.g., SS commands a higher $/WAR than LF).',
          'Aging: Position on the aging curve — 100% at peak (age 27 hitters, 26 pitchers), declining after.',
          'Control: Years of team control remaining, including contract type (pre-arb, arb, FA).',
          'Also includes a 3-year trajectory projection, market value estimate, and full written narrative.',
        ],
      },
    ],
  },
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: 'war',
    title: 'Wins Above Replacement',
    iconKey: 'assessment',
    description: 'WAR family metrics measuring player value in wins.',
    stats: [
      {
        name: 'WAR',
        fullName: 'Wins Above Replacement (v2)',
        scale: '−2 to 12+',
        applies: 'Player & Team',
        category: 'value',
        description:
          'The Cycle\'s WAR estimation, calibrated to FanGraphs\' fWAR methodology. Measures how many more wins a player contributes compared to a freely available replacement-level player.',
        formula:
          'Hitters: (wRAA + Baserunning + Positional Adj + Park Adj + Replacement Level) ÷ Runs Per Win\nPitchers: FIP-based, park-adjusted, leverage-weighted for relievers',
        benchmarks: [
          { label: '8+ WAR', desc: 'MVP caliber' },
          { label: '6+ WAR', desc: 'All-Star' },
          { label: '4+ WAR', desc: 'Very good starter' },
          { label: '2+ WAR', desc: 'Above average' },
          { label: '0–2 WAR', desc: 'Average to below average' },
          { label: '< 0 WAR', desc: 'Below replacement level' },
        ],
      },
      {
        name: 'Projected WAR',
        fullName: 'Regressed Full-Season WAR Projection',
        scale: '−2 to 12+',
        applies: 'Player',
        category: 'value',
        description:
          'What a player\'s WAR pace says he\'ll finish with — after regressing small samples toward the positional average. Used by CTS and Surplus Value so a hot two weeks doesn\'t look like an MVP season.',
        formula:
          'Hitters:  trust = G ÷ (G + 45);  projected = pace × trust + positional avg × (1 − trust), over 162 G\nPitchers: trust = IP ÷ (IP + 40), over 180 IP (SP) or 65 IP (RP)',
        details: [
          'A 4.0-WAR pace over 85 games projects near 5.5; the same pace over 15 games projects far lower.',
          'The regression prior is the average WAR at the player\'s position, not league average.',
        ],
      },
      {
        name: 'pWAR',
        fullName: 'Playoff WAR',
        scale: '−1 to 5+',
        applies: 'Player',
        category: 'value',
        description:
          'WAR calculated exclusively from postseason games. Same methodology, applied to October performance only.',
      },
    ],
  },
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: 'advanced-batting',
    title: 'Advanced Batting Metrics',
    iconKey: 'batting',
    description: 'Park-adjusted and context-neutral batting analytics.',
    stats: [
      { name: 'wRC+', fullName: 'Weighted Runs Created Plus', scale: '100 = league avg', applies: 'Player', category: 'batting', description: 'The gold standard for offense. Measures total offensive value, adjusted for park and league. 120 = 20% better than the average hitter.' },
      { name: 'OPS+', fullName: 'On-Base Plus Slugging Plus', scale: '100 = league avg', applies: 'Player', category: 'batting', description: 'Park-adjusted OPS. 100 is average; 150+ is elite.' },
      { name: 'wOBA', fullName: 'Weighted On-Base Average', scale: '.000–.500+', applies: 'Player', category: 'batting', description: 'Assigns proper linear weights to each offensive event (a HR is worth ~2× a single, not 4×). More accurate than OPS, and the primary anchor of CVR for hitters. League average ≈ .320.' },
      { name: 'ISO', fullName: 'Isolated Power', scale: '.000–.400+', applies: 'Player', category: 'batting', description: 'Measures raw power by stripping out singles. Formula: SLG − AVG.', benchmarks: [{ label: '.250+', desc: 'Elite power' }, { label: '.200', desc: 'Above average' }, { label: '.140', desc: 'Average' }, { label: '.100', desc: 'Below average' }] },
      { name: 'BABIP', fullName: 'Batting Average on Balls In Play', scale: '.000–.500+', applies: 'Player', category: 'batting', description: 'Batting average on balls put into play (excluding HR, K, sac bunts). League average ≈ .300 — a hitter far above/below that is often getting lucky/unlucky.' },
    ],
  },
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: 'batted-ball',
    title: 'Batted Ball & Pitch Quality',
    iconKey: 'radar',
    description: 'Exit velocity, launch angle, and pitch-level metrics from tracked pitch data.',
    stats: [
      {
        name: 'EV',
        fullName: 'Exit Velocity',
        scale: 'mph',
        applies: 'Player',
        category: 'statcast',
        description: 'How hard the ball leaves the bat. The single most predictive contact-quality input.',
        benchmarks: [
          { label: '95+', desc: 'Hard-hit — the standard damage threshold' },
          { label: '~88–89', desc: 'League-average EV' },
          { label: '110+', desc: 'Elite raw power territory' },
        ],
      },
      {
        name: 'LA',
        fullName: 'Launch Angle',
        scale: 'degrees',
        applies: 'Player',
        category: 'statcast',
        description: 'Vertical angle of the ball off the bat. Combined with EV it determines the likely outcome.',
        benchmarks: [
          { label: '< 10°', desc: 'Ground ball' },
          { label: '10–25°', desc: 'Line drive — highest hit probability' },
          { label: '25–50°', desc: 'Fly ball' },
          { label: '> 50°', desc: 'Pop up — near-automatic out' },
        ],
      },
      { name: 'Hard-Hit%', fullName: 'Hard-Hit Rate', scale: '% of batted balls', applies: 'Player', category: 'statcast', description: 'Share of batted balls at 95+ mph exit velocity. League average ≈ 38–40%.' },
      { name: 'Barrel', fullName: 'Barrel Rate', scale: '% of batted balls', applies: 'Player', category: 'statcast', description: 'The Cycle\'s barrel definition: 98+ mph exit velocity in the 26–30° launch window — the EV/LA combination that most often produces home runs. League average ≈ 6–8%.' },
      { name: 'Sweet Spot%', fullName: 'Sweet-Spot Rate', scale: '% of batted balls', applies: 'Player', category: 'statcast', description: 'Share of batted balls launched between 8° and 32° — the productive launch window.' },
      {
        name: 'Stuff+',
        fullName: 'Stuff Plus (pitch quality)',
        scale: '100 = avg · 40–200',
        applies: 'Pitcher',
        category: 'statcast',
        description:
          'Rates the raw nastiness of each pitch — velocity, induced vertical break, horizontal break, and whiff rate — against league baselines for that pitch type. 100 is league average; each 20 points ≈ one standard deviation.',
        formula:
          'Per pitch type: z-scores of velo, IVB, horizontal break, whiff% vs league baselines\n→ weighted per pitch type → overall Stuff+ = usage-weighted average',
        details: [
          'Baselines are recomputed from actual league data each season, so 100 always means "average this year".',
          'Requires at least 10 tracked pitches of a type before it\'s scored.',
          'A pitcher with a 130 Stuff+ slider has one of the best sliders in baseball.',
        ],
      },
    ],
  },
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: 'advanced-pitching',
    title: 'Advanced Pitching Metrics',
    iconKey: 'chart',
    description: 'Defense-independent and park-adjusted pitching analytics.',
    stats: [
      { name: 'ERA+', fullName: 'Adjusted Earned Run Average', scale: '100 = league avg', applies: 'Player', category: 'pitching', description: 'Park-adjusted ERA. 100 is average, higher is better. 150 = 50% better than average.' },
      { name: 'FIP', fullName: 'Fielding Independent Pitching', scale: 'ERA scale', applies: 'Player', category: 'pitching', description: 'Expected ERA based on strikeouts, walks/HBP, and home runs — the three outcomes a pitcher controls without his defense. The primary anchor of CVR for pitchers.' },
      { name: 'xFIP', fullName: 'Expected FIP', scale: 'ERA scale', applies: 'Player', category: 'pitching', description: 'FIP with home runs replaced by expected HR rate from fly-ball rate. More predictive over small samples.' },
      { name: 'SIERA', fullName: 'Skill-Interactive ERA', scale: 'ERA scale', applies: 'Player', category: 'pitching', description: 'The most sophisticated defense-independent metric. Accounts for how K, BB, and ground-ball rates interact.' },
      { name: 'FIP-', fullName: 'FIP Minus', scale: '100 = league avg', applies: 'Player', category: 'pitching', description: 'Park-adjusted FIP where 100 is average and lower is better.' },
      { name: 'K-BB%', fullName: 'Strikeout minus Walk Rate', scale: '0%–30%+', applies: 'Player', category: 'pitching', description: 'The best quick pitcher quality indicator — stuff (K%) combined with command (BB%).', benchmarks: [{ label: '25%+', desc: 'Elite' }, { label: '15%', desc: 'Good' }, { label: '10%', desc: 'Average' }, { label: '< 5%', desc: 'Below average' }] },
    ],
  },
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: 'contract',
    title: 'Contract & Market Metrics',
    iconKey: 'bank',
    description: 'Financial analytics for trade value and roster evaluation.',
    stats: [
      { name: 'Market Rate', fullName: 'Market Rate per WAR', scale: '$10M / WAR', applies: 'League', category: 'contract', description: 'The free-agent going rate for one win above replacement, used across CVR, ACVR, surplus value, and the trade engine. One 4-WAR season is worth roughly $40M on the open market.' },
      { name: 'Team Control', fullName: 'Years of Team Control', scale: 'FA to 6+ years', applies: 'Player', category: 'contract', description: 'Years before free agency. Combines contract years remaining with service-time estimates. More control = more surplus years = more trade value.' },
      { name: 'Contract Efficiency', fullName: 'Market Value / Salary Ratio', scale: '0 to 10+', applies: 'Player', category: 'contract', description: 'WAR-based market value ÷ salary. Pre-arb stars can exceed 30×.' },
      { name: 'Positional Market Rate', fullName: 'Position-Specific $/WAR', scale: 'varies by position', applies: 'Position', category: 'contract', description: 'The going rate for a win at each position. Premium positions (SS, C, SP) command a higher $/WAR because fewer players can fill the role. Used in DTV to contextualize whether a player is overpaid relative to his specific position market.' },
    ],
  },
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: 'projections',
    title: 'Projections & Aging',
    iconKey: 'trending',
    description: 'Forward-looking analytics for trajectory and future value.',
    stats: [
      { name: 'Aging Curve', fullName: 'Performance Aging Curve', scale: '0–100%', applies: 'Player', category: 'projection', description: 'Expected % of peak performance at a given age. Hitters peak around 26–27, pitchers around 25–26, with steeper decline for pitchers. Drives the Age Trajectory pillar of CTS and future-year projections in Surplus Value and DTV.' },
      { name: 'Future Value', fullName: 'Discounted Future Surplus', scale: '−$50M to $200M+', applies: 'Player', category: 'projection', description: 'Projects WAR forward through remaining control years via the aging curve, converts to dollars at $10M/WAR, subtracts escalating salary (3%/yr), and discounts at 8% annually — a dollar of surplus next year is worth less than one today.' },
      { name: '3yr Retention', fullName: '3-Year Performance Retention', scale: '0–100%', applies: 'Player', category: 'projection', description: 'Estimated % of current production retained over the next 3 years, from the aging curve.' },
    ],
  },
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: 'team',
    title: 'Team-Level Metrics',
    iconKey: 'groups',
    description: 'Aggregate team analytics combining individual player data.',
    stats: [
      { name: 'Team WAR', fullName: 'Roster Wins Above Replacement', scale: '0–80+', applies: 'Team', category: 'team', description: 'Sum of all rostered players\' individual WAR.' },
      { name: 'Team CVR', fullName: 'Roster Composite Value Rating', scale: '1–99', applies: 'Team', category: 'team', description: 'Playing-time-weighted average of all rostered players\' CVR — batters weighted by PA, pitchers by IP × 3. Answers "how talented is this roster?" independent of payroll.' },
      { name: 'Cycle Index', fullName: 'Team Market Efficiency (see Proprietary section)', scale: '1–99 · 50 = expected', applies: 'Team', category: 'team', description: 'Wins versus payroll-expected wins: 50 + (162-game pace wins − expected wins) × 1.5, where expected wins = 81 + (payroll − league-median payroll) ÷ $10M. The team-level counterpart to ACVR.' },
      { name: 'Positional Scarcity', fullName: 'Positional Scarcity Rating', scale: '0–10', applies: 'Position', category: 'team', description: 'How scarce quality players are at a position. C=9, SS/SP=8, CF/CL=7, 2B/3B=6, RF=5, LF/RP=4, 1B=3, DH=2. Feeds the Scarcity pillar of CTS.' },
    ],
  },
];

// Quick-search chips shown before the user types.
export const GLOSSARY_QUICK_CHIPS = ['CVR', 'ACVR', 'CTS', 'Cycle Index', 'Surplus', 'WAR', 'Stuff+', 'wRC+', 'FIP'];

export const GLOSSARY_FOOTNOTE =
  'CVR, ACVR, Cycle Index, CTS, Surplus Value, and DTV are original creations of The Cycle. ' +
  'Standard sabermetric metrics follow established formulas.';
